import { useState } from "react";
import { useAuthContext } from "../../context/AuthContext";
import LoginPageView from "./LoginPageView";
import useCustomEffect from "../../hooks/useCustomEffect";
import { useLanguageContext } from "../../context/LanguageContext";
import useEvaApi, { EvaApiCall } from "../../hooks/useEvaApi";

function LoginPage() {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const { translate } = useLanguageContext();

  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [discoverData, setDiscoverData] = useState(null);
  const [loginData, setLoginData] = useState({
    email: localStorage.getItem("remember_me_email") || "",
    password: "",
    tfaCode: "",
    loginWithSSO: localStorage.getItem("login_with_sso") ? true : false,
    rememberMe: localStorage.getItem("remember_me_email") ? true : false,
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * Initialize the component
   */
  useCustomEffect(() => {
    fetchAnnouncements();

    if (loginData.email) {
      onDiscover();
    }
  });

  /**
   * This callback will get the active announcements from the API
   */
  async function fetchAnnouncements() {
    await callEvaApi(
      new EvaApiCall("announcements")
        .setParams({ active_only: true })
        .setLoadingGroup("login_page_announcements_fetch")
        .setOnSuccess((response) => {
          setAnnouncements(response.data.data.records);
        }),
    );
  }

  /**
   * This method will update the email on input change
   * @param {event} e
   */
  function onEmailChange(e) {
    setLoginData({
      email: e.target.value,
      password: "",
      tfaCode: "",
      rememberMe: false,
    });
    setDiscoverData(null);
    localStorage.removeItem("remember_me_email");
  }

  /**
   * This method will update the email on input change
   * @param {event} e
   */
  function onPasswordChange(e) {
    setLoginData({ ...loginData, password: e.target.value });
  }

  /**
   * This method will update the email on input change
   * @param {event} e
   */
  function onTFACodeChange(e) {
    setLoginData({ ...loginData, tfaCode: e.target.value });
  }

  /**
   * This method will update the remember me state on input change
   * @param {bool} value
   */
  function onRememberMeChange(value) {
    setLoginData({ ...loginData, rememberMe: value });
    if (!value) {
      localStorage.removeItem("remember_me_email");
    } else {
      localStorage.setItem("remember_me_email", loginData.email);
    }
  }

  /**
   * This method will update the login with SSO state on input change
   * @param {bool} value
   */
  function onLoginWithSSOChange(value) {
    if (value) localStorage.setItem("login_with_sso", true);
    else localStorage.removeItem("login_with_sso");
    setLoginData({ ...loginData, loginWithSSO: value });
  }

  /**
   * This method will copy the QR Code secret to the clipboard
   */
  function onQRCodeClick() {
    navigator.clipboard.writeText(getQRCodeSecret());
    alert(translate("eva.main.general.copied_to_clipboard"));
  }

  /**
   * Gets the secret from the QR Code URL
   * @returns {string} The QR Code secret
   */
  function getQRCodeSecret() {
    const url = new URL(discoverData.tfa_qr_code_url);
    return url.searchParams.get("secret");
  }

  /**
   * This method will submit the form to log the user in
   */
  async function onSubmit() {
    setSubmitError(null);

    if (loginData.loginWithSSO) {
      authContext.initAzureLogin(discoverData.azure_config, loginData.email);
    } else {
      authContext.login(
        loginData.email,
        loginData.password,
        loginData.tfaCode,
        null,
        (error) => {
          setSubmitError(error);
        },
      );
    }
  }

  /**
   * This method will try to discover the user
   */
  async function onDiscover() {
    setSubmitError(null);

    authContext.discover(
      loginData.email,
      (data) => setDiscoverData(data),
      (error) => setSubmitError(error),
    );
  }

  /**
   * This method will request a password reset for the given email
   */
  async function onRequestPasswordReset() {
    await callEvaApi(
      new EvaApiCall(`password_reset/request`)
        .setMethod("POST")
        .setData({ email: loginData.email })
        .setAlertSuccess(true)
        .setAlertError(true)
        .setErrorState(setSubmitError)
        .setLoadingGroup("request_password_reset"),
    );
  }

  return (
    <LoginPageView
      loginData={loginData}
      discoverData={discoverData}
      announcements={announcements}
      onEmailChange={onEmailChange}
      onPasswordChange={onPasswordChange}
      onTFACodeChange={onTFACodeChange}
      onRememberMeChange={onRememberMeChange}
      onLoginWithSSOChange={onLoginWithSSOChange}
      onQRCodeClick={onQRCodeClick}
      onSubmit={onSubmit}
      onDiscover={onDiscover}
      onRequestPasswordReset={onRequestPasswordReset}
      submitError={submitError}
      showAnnouncements={showAnnouncements}
      setShowAnnouncements={setShowAnnouncements}
    />
  );
}

export default LoginPage;
